import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import { comunidadAdapter, VocesAdmin, VocesEditionSummary } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";

/** CSV con BOM y `;` — Excel en castellano lo abre directo en columnas, con tildes bien. */
function downloadVocesCsv(voces: VocesAdmin) {
  // Un texto que empieza con = + - @ (o tab/CR) Excel lo toma como fórmula aunque vaya entre
  // comillas: se le antepone ' para que quede como texto. Las respuestas son texto libre.
  const esc = (v: string) => `"${(/^[=+\-@\t\r]/.test(v) ? `'${v}` : v).replace(/"/g, '""')}"`;
  const header = ["Nombre", "Apellido", "Correo electrónico", "Sin nombre al compartir", "Respuesta", "Fecha"];
  const lines = voces.items.map((v) =>
    [
      v.name,
      v.surname,
      v.email,
      v.anonymous ? "Sí" : "No",
      v.answer,
      new Date(v.updatedAt).toLocaleString("es-AR"),
    ]
      .map(esc)
      .join(";"),
  );
  const csv = "\uFEFF" + [header.map(esc).join(";"), ...lines].join("\r\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `voces-de-la-red-edicion-${voces.editionNumber}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Panel de admin: respuestas a "Voces de la Red" por edición del newsletter, para armar la
 * edición siguiente. Se ven siempre con nombre; "Sin nombre al compartir" indica quién pidió
 * que no aparezca si su respuesta se publica.
 */
export const VocesDeLaRedPanel = () => {
  const [editions, setEditions] = useState<VocesEditionSummary[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [voces, setVoces] = useState<VocesAdmin | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    comunidadAdapter
      .listVocesEditions()
      .then((data) => {
        setEditions(data);
        setSelected(data[0]?.editionNumber ?? null);
        if (!data.length) setLoading(false);
      })
      .catch(() => {
        toastUtils.error("No se pudieron cargar las respuestas de Voces de la Red.");
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (selected === null) return;
    setLoading(true);
    comunidadAdapter
      .getVocesAdmin(selected)
      .then(setVoces)
      .catch(() => {
        setVoces(null);
        toastUtils.error("No se pudieron cargar las respuestas de esta edición.");
      })
      .finally(() => setLoading(false));
  }, [selected]);

  return (
    <div className="flex flex-col gap-4 rounded-[20px] bg-white p-6 shadow-[0_6px_20px_rgba(109,87,188,0.08)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="m-0 text-base font-bold text-[#2D2049]">Respuestas de Voces de la Red</h3>
          {editions.length > 0 ? (
            <select
              id="voces-edition-select"
              aria-label="Edición"
              className="mt-1 max-w-full rounded-lg border border-[#C9BCE8] bg-white px-2 py-1 text-sm text-[#2D2049]"
              value={selected ?? ""}
              onChange={(e) => setSelected(Number(e.target.value))}
            >
              {editions.map((e) => (
                <option key={e.editionNumber} value={e.editionNumber}>
                  Edición #{e.editionNumber} — {e.month} ({e.count} {e.count === 1 ? "respuesta" : "respuestas"})
                </option>
              ))}
            </select>
          ) : (
            !loading && (
              <p className="m-0 mt-1 text-sm text-gray-500">
                Todavía no hay ediciones publicadas con pregunta de Voces de la Red.
              </p>
            )
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          className="border-[#C9BCE8] text-[#665090]"
          disabled={!voces || voces.items.length === 0}
          onClick={() => voces && downloadVocesCsv(voces)}
        >
          <Download size={15} className="mr-1.5" />
          Descargar Excel
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><CircularProgress /></div>
      ) : voces ? (
        <>
          <div className="flex flex-col gap-1 rounded-xl bg-[#F5F1FF] px-4 py-3">
            <span className="text-sm font-semibold text-[#2D2049]">{voces.question}</span>
            <span className="text-xs text-gray-500">
              {voces.open
                ? "Recibiendo respuestas — cierra cuando publiques la edición siguiente."
                : "Cerrada: ya salió la edición siguiente."}
            </span>
          </div>
          {voces.items.length === 0 ? (
            <p className="m-0 text-sm text-gray-400">Todavía nadie respondió.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {voces.items.map((v) => (
                <div key={v.email} className="flex flex-col gap-1 rounded-xl border border-[#E3D9FF] px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-[#2D2049]">
                      {v.name} {v.surname}
                    </span>
                    {v.anonymous && (
                      <span className="rounded-full bg-[#EDE6FA] px-2 py-0.5 text-[11px] font-semibold text-[#4A3D73]">
                        Sin nombre al compartir
                      </span>
                    )}
                    <span className="text-xs text-gray-400">{new Date(v.updatedAt).toLocaleDateString("es-AR")}</span>
                  </div>
                  <p className="m-0 whitespace-pre-line text-sm text-[#4A3D73]">{v.answer}</p>
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default VocesDeLaRedPanel;
